import moment from 'moment'

/**
 *
 * 正規表現で日付がハイフン無しか確認する為の定数です。
 *
 * @type {String} 数字かつ8桁のみ
 * 例 20180515
 */
const number8 = /^\d{8}$/g

/**
 *
 * 正規表現で1以上の数字か確認する為の定数です。
 *
 * @type {String} 1以上かつ数字のみ *0以下は通しません
 * 例 20180515
 */
const numberOnly = /^([1-9]\d*|0)$/

/**
 *
 * ハイフン無しの日付フォーマットの定数です。
 *
 * @type {String} 通常使用する日付フォーマット
 */
const DATE_FORMAT = 'YYYYMMDD'

/**
 * ハイフン区切りの日付フォーマットの定数です。
 *
 * @type {String} カレンダーで使用する日付フォーマット
 */
const DATE_FORMAT_ON_INPUT_TAG = 'YYYY-MM-DD'

/**
 * 引数をハイフンありの日付へ変換して返す。
 *
 * 例 (20180101) return=2018-01-01
 *
 * @param  {String} value ハイフンなしの日付です。
 * @return {String}  ハイフンありの日付です。
 */

export function toInputValueFormat(value) {
  const result = String(value).match(number8)
  if (!result) {
    value = null
  } else {
    value = moment(value, DATE_FORMAT).format(DATE_FORMAT_ON_INPUT_TAG)
  }
  return value
}

/**
 * 引数をハイフンなしの日付へ変換して返す
 *
 * 例 (2018-01-01) return=20180101
 *
 * @param  {String} value ハイフンありの日付
 * @return {String}  ハイフンなしの日付
 */
export function toBasicDateFormat(value) {
  const result = String(value).match(/^\d{4}-?\d{2}-?\d{2}$/g)
  if (result === 'null') {
    value = null
  } else {
    value = moment(value, DATE_FORMAT_ON_INPUT_TAG).format(DATE_FORMAT)
    if (isNaN(value)) {
      value = null
    }
  }
  return value
}

/**
 * 日付文字列（yyyyMMdd）を読みやすい表記にフォーマットする
 *
 * @param {String} value
 */
export function toHumanFriendlyFormat(value) {
  if (!value) {
    return null
  }
  if (!value.match(number8)) {
    return null
  }

  return moment(value, DATE_FORMAT).format('YYYY年M月D日')
}

/**
 * x週間前を日付にする為の関数です
 *
 * 例 (20180107,1) return=20180101
 *
 * @param  {String} dateTo 期間検索末日
 * @param  {Number} weekCount 指定された週数
 * @return {String} 期間検索末日からx週間前の日付
 */
export function getStartDateByWeekCount(dateTo, weekCount) {
  let value
  const resultDateTo = String(dateTo).match(number8)
  const resultWeekCount = String(weekCount).match(numberOnly)
  if (!resultDateTo || !resultWeekCount) {
    value = null
  } else {
    value = moment(dateTo).subtract(weekCount, 'weeks')
    if (weekCount >= 1) {
      // momentのsubtractで週計算すると一日ずれるのでaddで戻す
      value = moment(value).add(1, 'days')
    }
    value = moment(value).format(DATE_FORMAT)
  }
  return value
}

/**
 * x週間後を日付にする為の関数です
 *
 * 例 (20180101,1) rerurn=20180107
 *
 * @param  {String} dateFrom 期間検索開始日
 * @param  {Number} weekCount 選択されている週数
 * @return {String}  x週間後の日付
 */
export function getEndDateByWeekCount(dateFrom, weekCount) {
  let value
  const resultDateFrom = String(dateFrom).match(number8)
  const resultWeekCount = String(weekCount).match(numberOnly)
  if (!resultDateFrom || !resultWeekCount) {
    value = null
  } else {
    value = moment(dateFrom).add(weekCount, 'weeks')
    if (weekCount >= 1) {
      // momenttで 月を計算すると一日ずれるので1日戻しています
      value = moment(value).subtract(1, 'days')
    }
    value = moment(value).format(DATE_FORMAT)
  }
  return value
}

/**
 * xヶ月前を日付にする為の関数です
 *
 * 例 (20181130,1) return=20181101
 *
 * @param  {String} dateTo 期間検索末日
 * @param  {String} monthCount 選択されている月数
 * @return {String}  xヶ月前の日付
 */
export function getStartDateByMonthCount(dateTo, monthCount) {
  let value
  const resultDateTo = String(dateTo).match(number8)
  const resultMonthCount = String(monthCount).match(numberOnly)
  if (!resultDateTo || !resultMonthCount) {
    value = null
  } else {
    value = moment(dateTo)
      // momenttで 月を計算すると一日ずれるので1日戻しています。
      .subtract(monthCount, 'months')
      .add(1, 'months')
      .startOf('month')
      .format(DATE_FORMAT)
  }
  return value
}

/**
 * xヶ月後を日付にする為の関数です。
 *
 * 例 dateFrom=(20181101,1) return=20181130
 *
 * @param  {String} dateFrom
 * @param  {String} monthCount 選択されている月数
 * @return {String}  xヶ月後の日付
 */
export function getEndDateByMonthCount(dateFrom, monthCount) {
  let value
  const resultMonthCount = String(monthCount).match(numberOnly)
  const resultDateFrom = String(dateFrom).match(number8)
  if (!resultDateFrom || !resultMonthCount) {
    value = null
  } else {
    value = moment(dateFrom)
      // momenttで月を計算すると一日ずれるので1日戻しています。
      .add(monthCount, 'months')
      .subtract(1, 'days')
      .endOf('month')
      .format(DATE_FORMAT)
  }
  return value
}

/**
 * 引数が選択可能な日付を越えない為の関数です。
 *
 * 例1 (20180515,20180131,20191201) return=20180515
 * 例2 (20180101),20180131,20191201) return=20180131
 * 例3 (20191231,20180101,20191201) return=20191201
 *
 * @param  {String} newVal 選択されている日付
 * @param  {String} minDate 選択出来る最小の日付
 * @param  {String} maxDate 選択出来る最大の日付
 * @return {String}  選択範囲内に収まっている日付
 */
export function dateLimit(newVal, minDate, maxDate) {
  // 選択範囲より日付が(少ないor多い)場合は選択範囲の限界へ戻します。
  const resultNewVal = String(newVal).match(number8)
  const resultMinDate = String(minDate).match(number8)
  const resultMaxDate = String(maxDate).match(number8)
  if (!resultNewVal || !resultMinDate || !resultMaxDate) {
    newVal = null
  } else {
    if (moment(minDate).isAfter(newVal)) {
      newVal = minDate
    }
    if (moment(maxDate).isBefore(newVal)) {
      newVal = maxDate
    }
  }
  return newVal
}

/**
 * x週間(前or後)が変更された場合、対になる日付が間隔を保つ為の関数です。
 *
 * 例1 (20180507,20180407,20180401) return=20180501
 * 例2 (20180501,20180401,20180407) return=20180507
 *
 * @param  {String} moveDate 間隔を保って移動させたい対になる日付
 * @param  {String} oldDate x週間(前or後)を変える前の日付
 * @param  {String} newDate x週間(前or後)を変えた後の日付
 * @return {String}  間隔を保って移動させた対になる日付
 */
export function weekMove(moveDate, oldDate, newDate) {
  // 日付が数字8桁のみか正規表現で確認します。
  const resultMoveDate = String(moveDate).match(number8)
  const resultOldDate = String(oldDate).match(number8)
  const resultNewDate = String(newDate).match(number8)
  if (!resultMoveDate || !resultOldDate || !resultNewDate) {
    moveDate = null
  } else {
    const days = moment(oldDate).diff(newDate, 'days')
    moveDate = moment(moveDate)
      .subtract(days, 'days')
      .format(DATE_FORMAT)
  }
  return moveDate
}

/**
 * xヶ月(前or後)が変更された場合、対になる日付が間隔を保つ為の関数です。
 *
 * 例1 (20180501,20180401,20180501) return=201800601
 * 例2 (20180501,20180401,20180301) return=20180401
 *
 * @param  {String} moveDate 間隔を保って移動させたい対になる日付
 * @param  {String} oldDate xヶ月(前or後)を変える前の日付
 * @param  {String} newDate xヶ月(前or後)を変えた後の日付
 * @return {String}  間隔を保って移動させた対になる日付
 */
export function monthMove(moveDate, oldDate, newDate) {
  let value
  // 日付が数字8桁のみか正規表現で確認します。
  const resultMoveDate = String(moveDate).match(number8)
  const resultOldDate = String(oldDate).match(number8)
  const resultNewDate = String(newDate).match(number8)
  if (!resultMoveDate || !resultOldDate || !resultNewDate) {
    value = null
  } else {
    oldDate = moment(oldDate, DATE_FORMAT).startOf('month')
    newDate = moment(newDate, DATE_FORMAT).startOf('month')
    const months = moment(oldDate).diff(newDate, 'months')
    value = moment(moveDate)
      .subtract(months, 'months')
      .format(DATE_FORMAT)
  }
  return value
}
